// Color mode (light/dark/auto) + compiling a shadcn-style theme JSON block to CSS vars (D12).
import { kebab, safeGet, safeSet, warn } from './util'

export type Mode = 'light' | 'dark' | 'auto'

const STYLE_ID = 'pagelet-theme-vars'
const STORE_KEY = 'pagelet-mode'
const THEME_COMPAT_STYLE_ID = 'pagelet-theme-compat'
const RUNTIME_STYLE_IDS = new Set(['pagelet-base-css', STYLE_ID, THEME_COMPAT_STYLE_ID])

function customProperties(style: CSSStyleDeclaration, into: Map<string, string>): void {
  for (let index = 0; index < style.length; index += 1) {
    const property = style.item(index)
    if (!property.startsWith('--')) continue
    const value = style.getPropertyValue(property).trim()
    if (value) into.set(property, value)
  }
}

/**
 * Bridge the common author pattern `:root { light vars } @media(prefers-color-scheme: dark)
 * { :root { dark vars } }` to Pagelet's explicit toolbar mode. Without this, forcing a mode that
 * differs from the OS produces a half-themed page: runtime surfaces switch, author surfaces don't.
 * Explicit [data-theme] pagelets are already authoritative and are left alone.
 */
export function injectThemeCompatibility(): void {
  if (document.getElementById(THEME_COMPAT_STYLE_ID)) return
  const light = new Map<string, string>()
  const dark = new Map<string, string>()
  let hasExplicitTheme = false

  const walk = (rules: CSSRuleList, mode: 'base' | 'light' | 'dark' = 'base') => {
    for (const rule of Array.from(rules)) {
      if (rule.type === CSSRule.STYLE_RULE) {
        const styleRule = rule as CSSStyleRule
        if (/\[data-theme(?:=|\])/i.test(styleRule.selectorText)) hasExplicitTheme = true
        const selectors = styleRule.selectorText.split(',').map((selector) => selector.trim())
        if (!selectors.some((selector) => selector === ':root' || selector === 'html')) continue
        customProperties(styleRule.style, mode === 'dark' ? dark : light)
        continue
      }

      const nested = 'cssRules' in rule ? (rule as CSSGroupingRule).cssRules : null
      if (!nested) continue
      if (rule.type === CSSRule.MEDIA_RULE) {
        const condition = (rule as CSSMediaRule).conditionText.toLowerCase()
        if (condition.includes('prefers-color-scheme') && condition.includes('dark')) {
          walk(nested, 'dark')
          continue
        }
        if (condition.includes('prefers-color-scheme') && condition.includes('light')) {
          walk(nested, 'light')
          continue
        }
      }
      walk(nested, mode)
    }
  }

  for (const sheet of Array.from(document.styleSheets)) {
    const owner = sheet.ownerNode as HTMLElement | null
    if (owner?.id && RUNTIME_STYLE_IDS.has(owner.id)) continue
    try {
      walk(sheet.cssRules)
    } catch {
      // Cross-origin stylesheets are opaque. Their own media query continues to follow the OS.
    }
  }
  if (hasExplicitTheme || dark.size === 0 || light.size === 0) return

  const declarations = (values: Map<string, string>) =>
    Array.from(values, ([property, value]) => `${property}:${value};`).join('')
  const style = document.createElement('style')
  style.id = THEME_COMPAT_STYLE_ID
  style.textContent =
    `html[data-theme="light"]{${declarations(light)}}` +
    `html[data-theme="dark"]{${declarations(dark)}}`
  document.head.appendChild(style)
}

/** Resolve and apply the initial color mode. Respects an explicit html[data-theme]. */
export function initMode(): void {
  const html = document.documentElement
  const explicit = html.getAttribute('data-theme')
  if (explicit === 'light' || explicit === 'dark') {
    html.setAttribute('data-pagelet-mode', explicit)
    return
  }
  setMode((safeGet(STORE_KEY) as Mode) ?? 'auto')
  // Track the OS while in auto so the page follows live system switches.
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (getMode() === 'auto') setMode('auto')
  })
}

export function getMode(): Mode {
  return (document.documentElement.getAttribute('data-pagelet-mode') as Mode) ?? 'auto'
}

export function setMode(mode: Mode): void {
  const resolved =
    mode === 'auto'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : mode
  const html = document.documentElement
  html.setAttribute('data-theme', resolved)
  html.setAttribute('data-pagelet-mode', mode)
  safeSet(STORE_KEY, mode)
  document.dispatchEvent(new CustomEvent('pagelet:themechange', { detail: { mode, resolved } }))
}

// Defence-in-depth: this block writes `--var:value` straight into a <style> with no escaping, so a
// non-hex value (e.g. `red;}body{…}` or `url(//x)`) would be a CSS-injection / exfil breakout. The
// server validates too, but the sink guards itself — only closed token keys + hex values reach it.
const THEME_TOKENS = new Set([
  'background', 'foreground', 'card', 'card-foreground',
  'primary', 'primary-foreground', 'secondary', 'secondary-foreground',
  'muted', 'muted-foreground', 'accent', 'accent-foreground', 'border', 'ring',
])
// Writer-friendly aliases — agents naturally reach for these names; silently dropping them
// leaves the page unthemed, so map them onto the canonical tokens instead.
const TOKEN_ALIASES: Record<string, string> = {
  text: 'foreground',
  'text-muted': 'muted-foreground',
  surface: 'card',
  'surface-foreground': 'card-foreground',
}
const HEX_VAL = /^#[0-9a-f]{3,8}$/i
const CSS_LEN_VAL = /^[0-9.]+(px|rem|em|%)?$/
const FONT_VAL = /^[a-zA-Z0-9 ,"'-]+$/

function colorVars(map: Record<string, string> | undefined): string[] {
  const out: string[] = []
  for (const [k, v] of Object.entries(map ?? {})) {
    const kk = TOKEN_ALIASES[kebab(k)] ?? kebab(k)
    if (THEME_TOKENS.has(kk) && typeof v === 'string' && HEX_VAL.test(v)) out.push(`--${kk}:${v};`)
  }
  return out
}

/** Compile a `<script data-pagelet="theme">{json}</script>` block to :root / [data-theme=dark] vars. */
export function applyTheme(): void {
  const el = document.querySelector('script[data-pagelet="theme"]')
  if (!el) return
  let theme: {
    colors?: Record<string, string>
    dark?: Record<string, string>
    radius?: string
    fontFamily?: string
    typography?: { fontFamily?: string }
  }
  try {
    theme = JSON.parse(el.textContent || '{}')
  } catch (e) {
    warn('theme', e)
    return
  }
  const root: string[] = colorVars(theme.colors)
  if (theme.radius && CSS_LEN_VAL.test(theme.radius)) root.push(`--radius:${theme.radius};`)
  const font = theme.fontFamily ?? theme.typography?.fontFamily
  if (font && FONT_VAL.test(font)) root.push(`--font-sans:${font};`)
  const dark: string[] = colorVars(theme.dark)

  const css =
    (root.length ? `:root{${root.join('')}}` : '') +
    (dark.length ? `[data-theme="dark"]{${dark.join('')}}` : '')
  if (!css) return

  let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null
  if (!style) {
    style = document.createElement('style')
    style.id = STYLE_ID
    document.head.appendChild(style)
  }
  style.textContent = css
}
